import {
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Divider,
  Flex,
  VStack,
} from '@chakra-ui/react';
import { Text } from '@diamond/shared/ui';
import {
  catalogMasking,
  formatCancelledReason,
  getStatusMap,
  statusFormatter,
} from '@diamond/shared/utils';
import {
  ORDER_DETAIL_CONSTANTS,
  SO_STATUS_CONFIG,
} from '@diamond/sol-buyer/constant';

import { CancelledOrdersSection } from './CancelledOrdersSection';
import { DeliveryOrderSection } from './DeliveryOrderSection';
import { OrderStatusBadge } from './OrderStatusBadge';
import { PendingItemsSection } from './PendingItemsSection';
import {
  OrderDetailStatusInfo,
  OrderRowProps,
  StatusBadgeConfig,
} from './types';

/**
 * Main accordion row for a single SO (Sales Order)
 *
 * COLLAPSED STATE:
 * - Shows SO order number (e.g., "SOL0104240001-1")
 * - Shows SO STATUS badge <- order detail item status
 *   Examples: "Diproses", "Dikirim", "Selesai", "Dibatalkan"
 *
 * EXPANDED STATE:
 * - Shows all DOs (Delivery Orders) for this SO
 * - Each DO shows DO STATUS <- DO_STATUS_CONFIG
 *   Examples: "Dikemas", "Dikirim", "Diterima", "Dibatalkan"
 * - Shows pending items (items not yet in any DO)
 * - Shows cancelled items (items cancelled before DO creation)
 */
export function OrderRow({
  order,
  distributionChannel,
  orderDetailStatusByDivision,
  orderDetailStatusByOrderId,
  overrideStatus,
  overrideReason,
  showDetails = true,
  renderInlineDetails = false,
  orderItemMetaById,
}: OrderRowProps) {
  const category = order.division
    ? catalogMasking(order.division, distributionChannel || '')
    : 'Order';
  const hasDeliveryOrders = order.delivery_orders.length > 0;

  const { STATUS } = ORDER_DETAIL_CONSTANTS;

  const fallbackStatus: OrderDetailStatusInfo = {
    status: STATUS.DRAFT,
    reason: null,
    sapNote: null,
  };
  const divisionKey = order.division?.toLowerCase() || '';
  const orderDetailStatus =
    orderDetailStatusByOrderId?.[order.order_id] ||
    (divisionKey && orderDetailStatusByDivision[divisionKey]) ||
    fallbackStatus;
  const isCancelledSO = orderDetailStatus.status === STATUS.CANCELLED;
  const isCancelledDisplay = overrideStatus === 'cancelled' || isCancelledSO;
  const resolvedCancellationReason =
    (overrideStatus === 'cancelled' ? overrideReason : undefined) ??
    order.cancel_reason ??
    (orderDetailStatus.status === STATUS.CANCELLED
      ? formatCancelledReason(
          orderDetailStatus.reason,
          orderDetailStatus.sapNote
        )
      : null) ??
    null;
  const displayOrderNumber =
    orderDetailStatus.subPoNumber || order.order_number;

  const statusConfig: StatusBadgeConfig = overrideStatus
    ? SO_STATUS_CONFIG[overrideStatus]
    : (() => {
        if (orderDetailStatus.status === STATUS.CANCELLED) {
          return SO_STATUS_CONFIG.cancelled;
        }
        const statusMap = getStatusMap(
          orderDetailStatus.status,
          orderDetailStatus.reason,
          orderDetailStatus.sapNote
        );
        const isProcessedStatus =
          orderDetailStatus.status === STATUS.PROCESSED ||
          orderDetailStatus.status === STATUS.PARTIALLY_PROCESSED;
        const isDeliveringStatus =
          orderDetailStatus.status === STATUS.OUT_FOR_DELIVERY;
        return {
          label: statusFormatter(
            orderDetailStatus.status,
            orderDetailStatus.reason,
            orderDetailStatus.sapNote
          ),
          color: statusMap.stateOptions.color,
          bg: isDeliveringStatus
            ? SO_STATUS_CONFIG.delivering.bg
            : statusMap.stateOptions.bg,
          dotColor: isProcessedStatus
            ? '#007AFF'
            : isDeliveringStatus
            ? SO_STATUS_CONFIG.delivering.dotColor
            : statusMap.stateOptions.color,
        };
      })();

  if (!showDetails) {
    return (
      <Box>
        <Flex
          justify="space-between"
          align="center"
          py={2}
          data-testid="order-row-simple"
        >
          <Box>
            <Text fontSize="sm" fontWeight="semibold" color="gray.800">
              {category}
            </Text>
            <Text fontSize="xs" color="#007AFF" noOfLines={1}>
              {displayOrderNumber}
            </Text>
          </Box>
          <Flex align="center" gap={2} flexShrink={0}>
            <OrderStatusBadge {...statusConfig} />
          </Flex>
        </Flex>
        {isCancelledDisplay && resolvedCancellationReason && (
          <Box mt={1} data-testid="order-row-cancel-reason">
            <Text fontWeight={400} fontSize="10px" color="#AEAEB2" mb={1}>
              Alasan Pembatalan
            </Text>
            <Text fontWeight={400} fontSize="12px" color="#373F41">
              {resolvedCancellationReason}
            </Text>
          </Box>
        )}
        {renderInlineDetails && hasDeliveryOrders && (
          <Box bg="white" borderRadius="md" p={0} mt={1}>
            <VStack
              align="stretch"
              spacing={0}
              divider={
                !isCancelledSO && order.pending_items.length > 0 ? undefined : (
                  <Divider borderColor="gray.200" />
                )
              }
            >
              {order.delivery_orders.map((deliveryOrder, index) => (
                <DeliveryOrderSection
                  key={deliveryOrder.id}
                  deliveryOrder={deliveryOrder}
                  index={index}
                  orderItemMetaById={orderItemMetaById}
                />
              ))}
              {!isCancelledSO && (
                <PendingItemsSection
                  items={order.pending_items}
                  orderItemMetaById={orderItemMetaById}
                />
              )}
              <CancelledOrdersSection cancelOrders={order.cancel_orders} />
            </VStack>
          </Box>
        )}
      </Box>
    );
  }

  return (
    <AccordionItem
      border="none"
      _last={{ borderBottom: 'none' }}
      data-testid="order-row-accordion"
    >
      <AccordionButton
        px={0}
        py={{ base: 2, md: 3 }}
        _hover={{ bg: 'gray.50' }}
        _expanded={{ bg: 'transparent' }}
        borderRadius="md"
      >
        <Flex flex={1} justify="space-between" align="center" gap={2}>
          <Box textAlign="left" minW={0}>
            <Text fontSize="sm" fontWeight="semibold" color="gray.800">
              {category}
            </Text>
            <Text fontSize="xs" color="#007AFF" noOfLines={1}>
              {displayOrderNumber}
            </Text>
          </Box>
          <Flex align="center" gap={2} flexShrink={0}>
            <OrderStatusBadge {...statusConfig} />
            <AccordionIcon color="gray.400" />
          </Flex>
        </Flex>
      </AccordionButton>

      <AccordionPanel px={0} pt={0} pb={{ base: 1, md: 2 }}>
        <Box bg="white" borderRadius="md" p={0} mt={1}>
          <VStack
            align="stretch"
            spacing={0}
            divider={
              !isCancelledSO && order.pending_items.length > 0 ? undefined : (
                <Divider borderColor="gray.200" />
              )
            }
          >
            {order.delivery_orders.map((deliveryOrder, index) => (
              <DeliveryOrderSection
                key={deliveryOrder.id}
                deliveryOrder={deliveryOrder}
                index={index}
                orderItemMetaById={orderItemMetaById}
              />
            ))}

            {/* Pending Items */}
            {!isCancelledSO && (
              <PendingItemsSection
                items={order.pending_items}
                orderItemMetaById={orderItemMetaById}
              />
            )}

            {/* Cancelled Orders */}
            <CancelledOrdersSection cancelOrders={order.cancel_orders} />
          </VStack>
        </Box>
        {isCancelledDisplay && resolvedCancellationReason && (
          <Box mt={1} data-testid="order-row-cancel-reason">
            <Text fontWeight={400} fontSize="10px" color="#AEAEB2" mb={1}>
              Alasan Pembatalan
            </Text>
            <Text fontWeight={400} fontSize="12px" color="#373F41">
              {resolvedCancellationReason}
            </Text>
          </Box>
        )}
      </AccordionPanel>
    </AccordionItem>
  );
}
